// ============================================================
// THE COMPANY — ConversationForm Component
// ============================================================

import { useState, useCallback } from 'react';
import { useCompanyStore } from '../../store/useCompanyStore';
import RichTextEditor from '../shared/RichTextEditor';

interface ConversationFormProps {
  roleId: string;
}

/** Check if Tiptap HTML content is effectively empty */
function isEmptyHtml(html: string): boolean {
  const stripped = html
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, '')
    .trim();
  return stripped.length === 0;
}

export default function ConversationForm({ roleId }: ConversationFormProps) {
  const addConversation = useCompanyStore((s) => s.addConversation);

  const [asked, setAsked] = useState('');
  const [answered, setAnswered] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Track editor keys to force remount (clear) after submit
  const [editorKey, setEditorKey] = useState(0);

  const canSubmit = !isEmptyHtml(asked) && !isEmptyHtml(answered) && !submitting;

  const handleAskedChange = useCallback((html: string) => {
    setAsked(html);
  }, []);

  const handleAnsweredChange = useCallback((html: string) => {
    setAnswered(html);
  }, []);

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await addConversation(roleId, { asked, answered });
      setAsked('');
      setAnswered('');
      setEditorKey((k) => k + 1); // Force editor remount to clear content
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section>
      <h3
        className="text-xs font-semibold uppercase tracking-wider mb-3"
        style={{ color: 'var(--text-muted)' }}
      >
        Add Conversation
      </h3>

      <div className="space-y-3">
        {/* "What I asked" */}
        <div>
          <label
            className="block text-xs font-medium mb-1.5"
            style={{ color: 'var(--text-secondary)' }}
          >
            What I asked
          </label>
          <RichTextEditor
            key={`asked-${editorKey}`}
            content={asked}
            onChange={handleAskedChange}
            placeholder="Paste or type what you asked ChatGPT..."
          />
        </div>

        {/* "What ChatGPT said" */}
        <div>
          <label
            className="block text-xs font-medium mb-1.5"
            style={{ color: 'var(--text-secondary)' }}
          >
            What ChatGPT said
          </label>
          <RichTextEditor
            key={`answered-${editorKey}`}
            content={answered}
            onChange={handleAnsweredChange}
            placeholder="Paste or type ChatGPT's response..."
          />
        </div>

        {/* Submit button */}
        <button
          type="button"
          onClick={handleSubmit}
          disabled={!canSubmit}
          className="w-full rounded-lg py-2.5 text-sm font-semibold cursor-pointer transition-all duration-150 disabled:opacity-40 disabled:cursor-not-allowed"
          style={{
            backgroundColor: canSubmit ? 'var(--accent)' : 'var(--bg-elevated)',
            color: canSubmit ? '#ffffff' : 'var(--text-muted)',
            border: '1px solid transparent',
          }}
          onMouseEnter={(e) => {
            if (canSubmit) {
              (e.target as HTMLElement).style.backgroundColor = 'var(--accent-hover)';
            }
          }}
          onMouseLeave={(e) => {
            if (canSubmit) {
              (e.target as HTMLElement).style.backgroundColor = 'var(--accent)';
            }
          }}
        >
          {submitting ? 'Saving...' : 'Save Conversation'}
        </button>
      </div>
    </section>
  );
}
